import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { ProjectCard } from './ProjectCard';
import { Button } from '../../../core/components/Button';
import { useProjects } from '../../../core/api';
import type { ApiProject } from '../../../core/api';
import { resolveImageUrl } from '../../../core/utils/image';

export interface FeaturedProjectsConfig {
    itemIds?: string[];
    title?: { en: string; ar: string } | string;
    description?: { en: string; ar: string } | string;
    backgroundImage?: string;
    useCustomBackground?: boolean;
    ctaUrl?: string;
}

interface FeaturedProjectsSliderProps {
    config?: FeaturedProjectsConfig;
}

export const FeaturedProjectsSlider = ({ config }: FeaturedProjectsSliderProps) => {
    const { t, i18n } = useTranslation();
    const scrollRef = useRef<HTMLDivElement>(null);
    const isRTL = i18n.language === 'ar';
    const lang = isRTL ? 'ar' : 'en';
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Determine effective config
    const itemIds = config?.itemIds || [];
    const hasIds = itemIds.length > 0;

    const rawTitle = config?.title;
    const title = typeof rawTitle === 'object' ? rawTitle[lang] : rawTitle;

    const rawDesc = config?.description;
    const description = typeof rawDesc === 'object' ? rawDesc[lang] : rawDesc;

    // Fetch featured projects from the API (either specific IDs or all featured)
    const { data: projectsResponse, isLoading } = useProjects({
        featured: !hasIds,
        ids: hasIds ? itemIds : undefined,
        limit: 10
    });
    const projects = (projectsResponse as any) || [];

    // Background Image Logic
    // Ensure we handle potential type discrepancies (e.g. string "true" vs boolean true)
    const isCustomBgEnabled = config?.useCustomBackground === true || String(config?.useCustomBackground) === 'true';
    const useCustomBg = isCustomBgEnabled && !!config?.backgroundImage;

    const firstProject: ApiProject | undefined = projects[0];

    // If custom background is set, use it as a single item array
    const sliderImages = useCustomBg && config?.backgroundImage
        ? [config.backgroundImage]
        : (firstProject
            ? (firstProject.gallery?.length > 0 ? firstProject.gallery.slice(0, 3) : [firstProject.image])
            : []);

    // Auto-rotate slider only if multiple images
    useEffect(() => {
        if (sliderImages.length <= 1) return;
        const timer = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % sliderImages.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [sliderImages.length]);

    const getAreaName = (project: ApiProject) => {
        return project.area?.name || project.area?.slug || '';
    };

    // Map projects to card props
    const featuredCards = projects.map((project: ApiProject) => ({
        id: project.id,
        title: project.title || project.slug,
        location: getAreaName(project),
        specs: {
            beds: project._count?.units || 0,
            baths: 0,
            area: project.areaRange || '—'
        },
        price: project.price,
        payment: project.paymentPlan || t('common.installments', 'Installments'),
        status: project.delivery || t('status.available', 'Available'),
        image: project.image,
        finishType: project.finish,
        deliveryDate: project.delivery,
        areaRange: project.areaRange,
        constructionStatus: (project as any).constructionStatus || undefined,
        propertyTypes: (project as any).categories?.map((c: any) => c.category?.translations?.[0]?.name || c.category?.slug).filter(Boolean) || []
    }));

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const cardWidth = 320 + 24;
            const actualDirection = isRTL ? (direction === 'left' ? 1 : -1) : (direction === 'left' ? -1 : 1);
            scrollRef.current.scrollBy({ left: cardWidth * actualDirection, behavior: 'smooth' });
        }
    };

    if (!isLoading && projects.length === 0) return null;

    return (
        <section className="relative bg-transparent flex flex-col">
            {/* 1. Banner Section (Top Half) */}
            <div className="relative h-[400px] sm:h-[450px] md:h-[500px] w-full bg-slate-900 overflow-hidden group">
                {/* Background Slider with Overlay */}
                <div className="absolute inset-0 z-0">
                    {isLoading ? (
                        <div className="absolute inset-0 bg-slate-800 animate-pulse" />
                    ) : (
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={currentImageIndex}
                                src={resolveImageUrl(sliderImages[currentImageIndex])}
                                alt="Background"
                                initial={{ opacity: 0, scale: 1.1 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 1.5, ease: 'easeInOut' }}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        </AnimatePresence>
                    )}
                    <div className="absolute inset-0 bg-slate-900/40" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 h-full flex flex-col pt-12 sm:pt-16 pointer-events-none">
                    {/* Header Content */}
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 h-full max-h-[60%] pointer-events-auto">
                        <div className="text-white max-w-2xl">
                            <h2 className={clsx(
                                "text-3xl sm:text-4xl md:text-5xl font-display font-bold mb-4 uppercase text-white drop-shadow-lg",
                                isRTL && "font-sans"
                            )}>
                                {title || (firstProject ? (firstProject.title || firstProject.slug) : (isRTL ? 'مشاريع مميزة' : 'Featured Projects'))}
                            </h2>
                            <p className="text-slate-100 text-sm sm:text-base max-w-xl font-light drop-shadow-md mb-6 whitespace-pre-line">
                                {description || (firstProject
                                    ? firstProject.description
                                    : (isRTL ? 'اكتشف أفضل المشاريع العقارية' : 'Discover the best real estate projects'))
                                }
                            </p>

                            {/* Explore Button */}
                            <Link to={config?.ctaUrl || "/explore"}>
                                <Button
                                    variant="primary"
                                    className="bg-white text-secondary hover:bg-slate-100 border-none shadow-xl"
                                >
                                    {isRTL ? 'عرض جميع الوحدات' : 'View All Units'}
                                </Button>
                            </Link>

                            {/* Slider Pagination Dots */}
                            {sliderImages.length > 1 && (
                                <div className="flex items-center gap-2 mt-8 pointer-events-auto">
                                    {sliderImages.map((_, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setCurrentImageIndex(idx)}
                                            className={clsx(
                                                "h-1.5 rounded-full transition-all duration-300 shadow-sm backdrop-blur-sm",
                                                idx === currentImageIndex
                                                    ? "bg-white w-8"
                                                    : "bg-white/40 hover:bg-white/60 w-4"
                                            )}
                                            aria-label={`Go to slide ${idx + 1}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Navigation Arrows */}
                        <div className="hidden md:flex items-center gap-4">
                            <button
                                onClick={() => scroll('left')}
                                className="w-12 h-12 rounded-full bg-white text-secondary hover:bg-slate-200 transition-colors flex items-center justify-center shadow-lg"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
                                </svg>
                            </button>
                            <button
                                onClick={() => scroll('right')}
                                className="w-12 h-12 rounded-full bg-white text-secondary hover:bg-slate-200 transition-colors flex items-center justify-center shadow-lg"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. Overlapping Cards Section (Bottom Half) */}
            <div className="relative z-20 -mt-32 sm:-mt-40 md:-mt-48 max-w-7xl mx-auto w-full px-4 sm:px-6 mb-16 pointer-events-none">
                {/* Mobile Navigation Arrows */}
                <div className="flex md:hidden justify-end gap-3 mb-4 pointer-events-auto">
                    <button
                        onClick={() => scroll('left')}
                        className="w-10 h-10 rounded-full bg-white text-secondary shadow-md flex items-center justify-center"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
                        </svg>
                    </button>
                    <button
                        onClick={() => scroll('right')}
                        className="w-10 h-10 rounded-full bg-white text-secondary shadow-md flex items-center justify-center"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                        </svg>
                    </button>
                </div>

                {/* Loading skeletons */}
                {isLoading && (
                    <div className="flex gap-6 pb-12 pointer-events-auto">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="min-w-[280px] sm:min-w-[340px] h-[360px] rounded-xl bg-slate-200 animate-pulse" />
                        ))}
                    </div>
                )}

                {!isLoading && featuredCards.length > 0 && (
                    <div
                        ref={scrollRef}
                        className="flex overflow-x-auto gap-6 pb-12 snap-x snap-mandatory scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 pointer-events-auto"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {featuredCards.map((card: any, index: number) => (
                            <motion.div
                                key={card.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <ProjectCard
                                    project={card}
                                    linkTo={`/projects/${projects[index]?.slug || card.id}`}
                                    className="min-w-[280px] sm:min-w-[340px] snap-center"
                                    type="Project"
                                    layout="project"
                                />
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};
